import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material';
import {useDownloadProduction} from '../../../hooks/productions/useProductions';
import '../Demo.scss';

const DownloadProductionApiTest: React.FC = () => {
  // Query Parameters
  const [sourcePath, setSourcePath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\Productions\\dndproject3\\previews\\Archives\\ProductionSmall_ProductionArchive'
  );
  const [fileName, setFileName] = useState<string>('ProductionSmall');

  const [downloadResult, setDownloadResult] = useState<string>('');

  const downloadMutation = useDownloadProduction(
    (data) => {
      setDownloadResult(
        `✅ Download successful! File: ${data.filename}, Content-Type: ${data.contentType}`
      );
    },
    (error) => {
      setDownloadResult(`❌ Download failed: ${error.message}`);
    }
  );

  const handleDownload = () => {
    downloadMutation.mutate({
      sourcePath,
      fileName,
    });
  };

  const handleReset = () => {
    setDownloadResult('');
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          📥 Download Production API Demo
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Test the download production endpoint to download production files
          (binary content)
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Download Production
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/downloads
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Download production file (binary content) with query parameters for
            sourcePath and fileName. Returns ZIP file with content-disposition
            header.
          </Typography>
        </CardContent>
      </Card>
      {/* Query Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <TextField
              label="Source Path"
              value={sourcePath}
              onChange={(e) => setSourcePath(e.target.value)}
              fullWidth
              size="small"
              placeholder="\\\\server\\path\\to\\production"
            />
            <TextField
              label="File Name"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              fullWidth
              size="small"
              placeholder="ProductionSmall"
            />
          </Box>
        </CardContent>
      </Card>
      {/* Actions */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
            <Button
              variant="contained"
              onClick={handleDownload}
              disabled={downloadMutation.isPending}
              sx={{minWidth: 120}}
              data-testid="download-production-api-test-button">
              {downloadMutation.isPending ? 'Downloading...' : 'Download'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={downloadMutation.isPending}
              data-testid="download-production-api-test-reset-button">
              Reset
            </Button>
          </Box>

          {downloadMutation.isPending && (
            <Box sx={{marginBottom: 2}}>
              <Typography
                className="typo-text body3_size"
                sx={{marginBottom: 1}}
              >
                Downloading file...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {downloadResult && (
            <Alert
              severity={downloadResult.includes('✅') ? 'success' : 'error'}
              sx={{marginTop: 2}}
            >
              {downloadResult}
            </Alert>
          )}
        </CardContent>
      </Card>
      {/* API Details */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            API Details
          </Typography>

          <Box sx={{marginBottom: 2}}>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Response Headers:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              content-disposition: attachment; filename=ProductionSmall;
              filename*=UTF-8''ProductionSmall
              <br />
              content-type: application/zip
              <br />
              content-length: 171888
            </Box>
          </Box>

          <Box>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Response Format:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              Binary content (ZIP file)
              <br />
              The file will be automatically downloaded when the request
              succeeds.
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DownloadProductionApiTest;
